import { RealtimeChannel, RealtimePostgresInsertPayload } from '@supabase/supabase-js';
import { supabase } from './client';
import { BROADCAST_EVENT_NAME } from '../utils/constants';

export interface BroadcastMediaPayload {
  url?: string;           // New: base name for media bundle (e.g., "POE_who_are_you_whats_your_story")
  video_file?: string;    // Legacy: direct video filename
  audio_file?: string;    // Legacy: direct audio filename
}

// Database row type for media_notifications table
interface MediaNotificationRow {
  id: string;
  user_id: string;
  url: string | null;
  video_file: string | null;
  audio_file: string | null;
  created_at: string;
}

export function getChannelName(userUuid: string): string {
  return `user:${userUuid}:media`;
}

/**
 * Validate that the payload has either:
 * - url (new format for media bundle discovery)
 * - OR both video_file and audio_file (legacy format)
 */
function isValidPayload(payload: BroadcastMediaPayload): boolean {
  if (payload?.url) {
    return true;
  }
  if (payload?.video_file && payload?.audio_file) {
    return true;
  }
  return false;
}

/**
 * Subscribe to media notifications via both:
 * 1. Postgres Changes (for server-to-client via n8n database inserts)
 * 2. Broadcast (for client-to-client testing)
 */
export function subscribeToBroadcast(
  userUuid: string,
  onMedia: (payload: BroadcastMediaPayload) => void
): RealtimeChannel {
  const channelName = getChannelName(userUuid);
  console.log('[Media] Subscribing to channel:', channelName, 'userUuid:', userUuid);

  const channel = supabase
    .channel(channelName, {
      config: {
        broadcast: { self: true },
      },
    })
    // Listen for database inserts (primary mechanism for n8n notifications)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'media_notifications',
        filter: `user_id=eq.${userUuid}`,
      },
      (payload: RealtimePostgresInsertPayload<MediaNotificationRow>) => {
        console.log('[Media] Database notification received:', payload);
        const row = payload.new;
        const mediaPayload: BroadcastMediaPayload = {
          url: row.url ?? undefined,
          video_file: row.video_file ?? undefined,
          audio_file: row.audio_file ?? undefined,
        };
        if (!isValidPayload(mediaPayload)) {
          console.warn('[Media] Received notification with invalid payload:', row);
          return;
        }
        if (mediaPayload.url) {
          console.log('[Media] Media bundle ready:', mediaPayload.url);
        } else {
          console.log('[Media] Legacy media ready:', mediaPayload.video_file, mediaPayload.audio_file);
        }
        onMedia(mediaPayload);
      }
    )
    // Also listen for broadcasts (for client-to-client testing)
    .on('broadcast', { event: '*' }, (message) => {
      console.log('[Broadcast] RAW event received:', message);
    })
    .on(
      'broadcast',
      { event: BROADCAST_EVENT_NAME },
      (message) => {
        const payload = message.payload as BroadcastMediaPayload;
        if (!isValidPayload(payload)) {
          console.warn('[Broadcast] Received event with invalid payload:', message);
          return;
        }
        if (payload.url) {
          console.log('[Broadcast] Media bundle ready:', payload.url);
        } else {
          console.log('[Broadcast] Legacy media ready:', payload.video_file, payload.audio_file);
        }
        onMedia(payload);
      }
    )
    .subscribe((status, err) => {
      console.log('[Media] Channel status:', status, err ?? '');
    });

  return channel;
}

export function unsubscribeFromBroadcast(channel: RealtimeChannel): void {
  channel.unsubscribe();
}

// Test function - expose globally for debugging
export function testBroadcast(channel: RealtimeChannel): void {
  console.log('[Broadcast] Sending test broadcast from client...');
  channel.send({
    type: 'broadcast',
    event: 'media-ready',
    payload: { url: 'client_test_bundle' },
  });
}
